import { useState, useEffect } from "react";
import { X, Search, Plus, Globe, Lock, Check, ListChecks } from "lucide-react";
import { useBurrow } from "./BurrowStore";
import { ResponsiveSheet } from "./ResponsiveSheet";

interface CollectionPickerProps {
  open: boolean;
  onClose: () => void;
  thoughtIds: string[];
  title?: string;
  /**
   * Fired after one or more collections have been added. `count` is how many
   * collections the thought(s) were added to. Lets callers (e.g. the resurface
   * deck) advance to the next card / resolve the thought.
   *
   * May return a reversal for whatever side effect the caller performed; it's
   * folded into this picker's own "Undo" toast so one tap backs out both. The
   * resurface deck needs this — the picker alone only undoes the membership,
   * not the resurface flag the deck cleared.
   */
  onAdded?: (count: number) => (() => void) | void;
}

export function CollectionPicker({ open, onClose, thoughtIds, title, onAdded }: CollectionPickerProps) {
  const { state, addToCollection, bulkAddToCollection, createCollection, removeFromCollection, removeTag, withdrawFromCommons, showToast } = useBurrow();
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  // Default behaviour is single click-to-add (popup closes). Toggling this on
  // lets the user tick several collections and commit them together.
  const [multi, setMulti] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Reset transient UI each time the picker opens for a fresh card.
  useEffect(() => {
    if (open) {
      setSearch("");
      setCreating(false);
      setNewName("");
      setMulti(false);
      setSelected(new Set());
    }
  }, [open]);

  const filtered = state.collections.filter((c) =>
    !search.trim() || c.name.toLowerCase().includes(search.toLowerCase())
  );

  const isSingle = thoughtIds.length === 1;

  const addOne = (collectionId: string) => {
    if (isSingle) addToCollection(thoughtIds[0], collectionId);
    else bulkAddToCollection(thoughtIds, collectionId);
  };

  // Build a closure that reverses one add: drop the thought(s) from the
  // collection, and strip the collection-name label that add*ToCollection
  // auto-applied — but only from thoughts that didn't already have it.
  // Captured from pre-add state, so call it *before* addOne mutates the store.
  const buildUndo = (collectionId: string) => {
    const col = state.collections.find((c) => c.id === collectionId);
    const label = col ? col.name.trim().toLowerCase() : null;
    const isPublicCol = !!col?.is_public;
    // Snapshot which cards were public BEFORE the add. Undoing an add to a
    // public collection would otherwise strand a newly-public card in the
    // commons forever as anonymous (once-public sticks) — those get routed
    // through withdraw, which returns them as fresh private cards. Cards that
    // were already public elsewhere just leave this collection normally.
    const wasPublic = new Map(
      thoughtIds.map((id) => [
        id,
        state.thoughts.find((t) => t.id === id)?.visibility === "public",
      ])
    );
    const newlyLabeled = label
      ? thoughtIds.filter((id) => {
          const t = state.thoughts.find((th) => th.id === id);
          return t ? !(t.labels ?? []).includes(label) : false;
        })
      : [];
    return () => {
      void (async () => {
        for (const id of thoughtIds) {
          if (isPublicCol && !wasPublic.get(id)) {
            const newId = await withdrawFromCommons(id, { silent: true });
            if (newId) {
              // Withdraw already removed the membership; the clone carried the
              // auto-added label over, so strip it there.
              if (label && newlyLabeled.includes(id)) removeTag(newId, label);
              continue;
            }
            // Withdraw declined (e.g. the insert hadn't landed and the card is
            // still private) — fall through to the plain removal.
          }
          removeFromCollection(id, collectionId);
          if (label && newlyLabeled.includes(id)) removeTag(id, label);
        }
      })();
    };
  };

  // Single-tap add: fire, name the toast (with Undo), tell the caller, close.
  const handleSelect = (collectionId: string) => {
    const undo = buildUndo(collectionId);
    addOne(collectionId);
    const callerUndo = onAdded?.(1);
    const col = state.collections.find((c) => c.id === collectionId);
    showToast(col ? `Added to "${col.name}".` : "Added to collection.", {
      label: "Undo",
      onAction: () => {
        undo();
        callerUndo?.();
      },
    });
    onClose();
  };

  const toggleSelected = (collectionId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(collectionId)) next.delete(collectionId);
      else next.add(collectionId);
      return next;
    });
  };

  const handleCommitMulti = () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    const undos = ids.map(buildUndo);
    ids.forEach(addOne);
    const callerUndo = onAdded?.(ids.length);
    // Our summary toast runs last, so it wins over the per-add toasts; Undo
    // reverses every collection this committed.
    showToast(`Added to ${ids.length} collection${ids.length > 1 ? "s" : ""}.`, {
      label: "Undo",
      onAction: () => {
        undos.forEach((u) => u());
        callerUndo?.();
      },
    });
    onClose();
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    const col = await createCollection(newName.trim());
    addOne(col.id);
    showToast(`Added to "${col.name}".`);
    setNewName("");
    setCreating(false);
    onAdded?.(1);
    onClose();
  };

  // Which collections already contain these thoughts?
  const containingIds = new Set(
    state.collections
      .filter((c) => thoughtIds.every((id) => c.card_ids.includes(id)))
      .map((c) => c.id)
  );

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      label={title || "Add to collection"}
      desktop="center"
      desktopWidth="max-w-[420px]"
      zIndex={60}
    >
            {/* Header */}
            <div className="flex items-center justify-between p-5 pb-0">
              <h2
                className="text-primary"
                style={{ fontFamily: "'Fraunces', Georgia, serif", fontSize: "1.1rem", fontWeight: 400 }}
              >
                {title || (isSingle ? "Add to collection" : `Sort ${thoughtIds.length} thoughts`)}
              </h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setMulti((m) => !m); setSelected(new Set()); }}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-all ${
                    multi
                      ? "bg-accent/12 text-accent"
                      : "text-muted-foreground/60 hover:text-accent hover:bg-accent/8"
                  }`}
                  style={{ fontSize: "0.74rem" }}
                  title="Add to several collections at once"
                >
                  <ListChecks size={13} />
                  {multi ? "Selecting" : "Select"}
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/30 transition-all"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="px-5 pt-4 pb-3">
              <div className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-border/40 bg-background">
                <Search size={14} className="text-muted-foreground shrink-0" />
                <input
                  type="text"
                  placeholder="Search collections..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-transparent outline-none w-full text-foreground placeholder:text-muted-foreground/50"
                  style={{ fontSize: "0.85rem", fontFamily: "'Fraunces', Georgia, serif" }}
                  autoFocus
                />
              </div>
            </div>

            {/* Collection list */}
            <div className="max-h-[280px] overflow-y-auto px-3 pb-2">
              {filtered.map((col) => {
                const alreadyIn = containingIds.has(col.id);
                const isSelected = selected.has(col.id);
                const onClick = () => {
                  if (alreadyIn) return;
                  if (multi) toggleSelected(col.id);
                  else handleSelect(col.id);
                };
                return (
                  <button
                    key={col.id}
                    onClick={onClick}
                    disabled={alreadyIn}
                    className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all text-left ${
                      alreadyIn
                        ? "opacity-50 cursor-not-allowed"
                        : isSelected
                          ? "bg-accent/10 cursor-pointer"
                          : "hover:bg-muted/30 cursor-pointer"
                    }`}
                  >
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: col.cover_color || "var(--accent)" }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-foreground truncate" style={{ fontSize: "0.88rem" }}>
                          {col.name}
                        </span>
                        {col.is_public ? (
                          <Globe size={11} className="text-accent shrink-0" />
                        ) : (
                          <Lock size={11} className="text-muted-foreground/40 shrink-0" />
                        )}
                      </div>
                      <span className="text-muted-foreground/50" style={{ fontSize: "0.72rem" }}>
                        {col.card_count} thoughts
                      </span>
                    </div>
                    {multi && !alreadyIn ? (
                      <div
                        className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-all ${
                          isSelected
                            ? "bg-accent border-accent text-accent-foreground"
                            : "border-border/60"
                        }`}
                      >
                        {isSelected && <Check size={12} />}
                      </div>
                    ) : (
                      alreadyIn && <Check size={14} className="text-accent shrink-0" />
                    )}
                  </button>
                );
              })}

              {filtered.length === 0 && (
                <p className="text-center text-muted-foreground/50 py-6" style={{ fontSize: "0.85rem" }}>
                  No collections found.
                </p>
              )}
            </div>

            {/* Footer — commit multiselect, or create a new collection */}
            <div className="border-t border-border/30 p-4">
              {multi && selected.size > 0 ? (
                <button
                  onClick={handleCommitMulti}
                  className="w-full py-2.5 rounded-xl bg-accent text-accent-foreground transition-all hover:opacity-90 flex items-center justify-center gap-2"
                  style={{ fontSize: "0.85rem" }}
                >
                  <Check size={15} />
                  Add to {selected.size} collection{selected.size > 1 ? "s" : ""}
                </button>
              ) : creating ? (
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleCreate();
                      if (e.key === "Escape") { setCreating(false); setNewName(""); }
                    }}
                    placeholder="Collection name..."
                    className="flex-1 px-3 py-2 rounded-xl border border-border/40 bg-background text-foreground placeholder:text-muted-foreground/40 outline-none focus:border-accent/40"
                    style={{ fontSize: "0.85rem", fontFamily: "'Fraunces', Georgia, serif" }}
                    autoFocus
                  />
                  <button
                    onClick={handleCreate}
                    disabled={!newName.trim()}
                    className="px-4 py-2 rounded-xl bg-accent text-accent-foreground transition-all hover:opacity-90 disabled:opacity-40"
                    style={{ fontSize: "0.82rem" }}
                  >
                    Create
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setCreating(true)}
                  className="w-full py-2.5 rounded-xl border border-dashed border-border/50 text-muted-foreground hover:text-accent hover:border-accent/40 transition-all flex items-center justify-center gap-2"
                  style={{ fontSize: "0.85rem" }}
                >
                  <Plus size={15} />
                  New Collection
                </button>
              )}
            </div>
    </ResponsiveSheet>
  );
}
